"use client"

import * as React from 'react'
import Link from 'next/link'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { SortingState } from '@tanstack/react-table'
import type { FilterValues } from '@open-mercato/ui/backend/FilterBar'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { Page, PageBody, PageHeader } from '@open-mercato/ui/backend/Page'
import { DynamicTable } from '@open-mercato/ui/backend/DynamicTable'
import { RowActions } from '@open-mercato/ui/backend/RowActions'
import { LoadingMessage, ErrorMessage } from '@open-mercato/ui/backend/detail'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'
import { useGuardedMutation } from '@open-mercato/ui/backend/injection/useGuardedMutation'
import { readApiResultOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { buildCrudQuery, createCrud, deleteCrud, updateCrud } from '@open-mercato/ui/backend/utils/crud'
import { Button } from '@open-mercato/ui/primitives/button'
import { FleetVehicleDialog, toFleetVehicleFormValues } from '../lib/fleetForm'
import {
  buildFleetColumns,
  buildFleetFilters,
  type FleetListResponse,
  type FleetVehicleListItem,
} from '../lib/fleetTable'

const DEFAULT_PAGE_SIZE = 25

function buildFleetListParams(
  page: number,
  search: string,
  filterValues: FilterValues,
  sorting: SortingState,
) {
  const sort = sorting[0]
  return {
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    q: search.trim() || undefined,
    type: typeof filterValues.type === 'string' ? filterValues.type : undefined,
    capability: Array.isArray(filterValues.capability) ? filterValues.capability : undefined,
    euroClass: typeof filterValues.euroClass === 'string' ? filterValues.euroClass : undefined,
    gpsProvider: typeof filterValues.gpsProvider === 'string' ? filterValues.gpsProvider : undefined,
    isActive: typeof filterValues.isActive === 'boolean' ? filterValues.isActive : undefined,
    sortField: sort?.id,
    sortDir: sort?.desc ? 'desc' : 'asc',
  }
}

function normalizeInlineDraft(draft: Record<string, unknown>): Record<string, unknown> {
  const next: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(draft)) {
    if (Array.isArray(value)) {
      next[key] = value
      continue
    }
    if (typeof value === 'string') {
      const trimmed = value.trim()
      next[key] = trimmed.length > 0 ? trimmed : null
      continue
    }
    next[key] = value
  }
  return next
}

async function fetchFleetList(params: ReturnType<typeof buildFleetListParams>): Promise<FleetListResponse> {
  return readApiResultOrThrow<FleetListResponse>(`/api/road/vehicles?${buildCrudQuery(params)}`, undefined, {
    errorMessage: 'Failed to fetch fleet vehicles.',
  })
}

export default function FleetListPage() {
  const t = useT()
  const scopeVersion = useOrganizationScopeVersion()
  const queryClient = useQueryClient()
  const { confirm, ConfirmDialogElement } = useConfirmDialog()
  const { runMutation } = useGuardedMutation({ contextId: 'fleet-list' })

  const [page, setPage] = React.useState(1)
  const [search, setSearch] = React.useState('')
  const [sorting, setSorting] = React.useState<SortingState>([{ id: 'registrationPlate', desc: false }])
  const [filterValues, setFilterValues] = React.useState<FilterValues>({})
  const [createOpen, setCreateOpen] = React.useState(false)
  const [createPending, setCreatePending] = React.useState(false)

  const params = React.useMemo(
    () => buildFleetListParams(page, search, filterValues, sorting),
    [filterValues, page, search, sorting],
  )

  const fleetQuery = useQuery({
    queryKey: ['fleet', 'vehicles', scopeVersion, params],
    queryFn: () => fetchFleetList(params),
  })

  const refreshFleet = React.useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['fleet', 'vehicles'] })
    await queryClient.invalidateQueries({ queryKey: ['fleet', 'vehicle'] })
  }, [queryClient])

  const handleInlineSave = React.useCallback(async (row: FleetVehicleListItem, draft: Record<string, unknown>) => {
    await runMutation({
      context: { kind: 'road.vehicle', record: { id: row.id } },
      mutationPayload: draft,
      operation: async () => {
        await updateCrud(`road/vehicles/${row.id}`, normalizeInlineDraft(draft), {
          errorMessage: t('fleet.messages.updateError', 'Failed to update vehicle.'),
        })
      },
    })
    flash(t('fleet.messages.updateSuccess', 'Vehicle updated.'), 'success')
    await refreshFleet()
  }, [refreshFleet, runMutation, t])

  const handleActiveToggle = React.useCallback(async (row: FleetVehicleListItem, next: boolean) => {
    try {
      await runMutation({
        context: { kind: 'road.vehicle', record: { id: row.id } },
        mutationPayload: { isActive: next },
        operation: async () => {
          await updateCrud(`road/vehicles/${row.id}`, { isActive: next }, {
            errorMessage: t('fleet.messages.updateError', 'Failed to update vehicle.'),
          })
        },
      })
      flash(t('fleet.messages.updateSuccess', 'Vehicle updated.'), 'success')
      await refreshFleet()
    } catch {
      flash(t('fleet.messages.updateError', 'Failed to update vehicle.'), 'error')
    }
  }, [refreshFleet, runMutation, t])

  const handleDelete = React.useCallback(async (row: FleetVehicleListItem) => {
    const approved = await confirm({
      title: t('fleet.confirm.deleteTitle', 'Delete vehicle?'),
      text: t(
        'fleet.confirm.deleteMessage',
        'This will archive the vehicle and keep audit history intact.',
      ),
      confirmText: t('fleet.actions.delete', 'Delete'),
      cancelText: t('common.cancel', 'Cancel'),
      variant: 'destructive',
    })
    if (!approved) return
    try {
      await runMutation({
        context: { kind: 'road.vehicle', record: { id: row.id } },
        mutationPayload: { id: row.id },
        operation: async () => {
          await deleteCrud(`road/vehicles/${row.id}`, {
            errorMessage: t('fleet.messages.deleteError', 'Failed to delete vehicle.'),
          })
        },
      })
      flash(t('fleet.messages.deleteSuccess', 'Vehicle deleted.'), 'success')
      await refreshFleet()
    } catch {
      flash(t('fleet.messages.deleteError', 'Failed to delete vehicle.'), 'error')
    }
  }, [confirm, refreshFleet, runMutation, t])

  const columns = React.useMemo(
    () => buildFleetColumns(t, {
      onToggleActive: (row, next) => {
        void handleActiveToggle(row, next)
      },
    }),
    [handleActiveToggle, t],
  )

  const filters = React.useMemo(() => buildFleetFilters(t), [t])

  if (fleetQuery.isLoading && !fleetQuery.data) {
    return (
      <Page>
        <PageHeader
          title={t('fleet.pages.list.title', 'Fleet')}
          description={t('fleet.pages.list.description', 'Road fleet master data for dispatchers.')}
        />
        <PageBody>
          <LoadingMessage label={t('fleet.messages.loading', 'Loading fleet…')} />
        </PageBody>
      </Page>
    )
  }

  if (fleetQuery.isError || !fleetQuery.data) {
    return (
      <Page>
        <PageHeader
          title={t('fleet.pages.list.title', 'Fleet')}
          description={t('fleet.pages.list.description', 'Road fleet master data for dispatchers.')}
        />
        <PageBody>
          <ErrorMessage
            label={t('fleet.messages.loadErrorTitle', 'Could not load fleet')}
            description={t('fleet.messages.loadError', 'Try again in a moment or refresh the page.')}
          />
        </PageBody>
      </Page>
    )
  }

  return (
    <Page>
      <PageHeader
        title={t('fleet.pages.list.title', 'Fleet')}
        description={t('fleet.pages.list.description', 'Road fleet master data for dispatchers.')}
        actions={(
          <Button type="button" onClick={() => setCreateOpen(true)}>
            {t('fleet.actions.addVehicle', 'Add vehicle')}
          </Button>
        )}
      />
      <PageBody>
        <DynamicTable
          title={t('fleet.pages.list.tableTitle', 'Vehicles')}
          columns={columns}
          data={fleetQuery.data.items}
          onSaveEdit={handleInlineSave}
          sortable={true}
          sorting={sorting}
          onSortingChange={(next) => {
            setSorting(next)
            setPage(1)
          }}
          pagination={{
            page: fleetQuery.data.page,
            pageSize: fleetQuery.data.pageSize,
            total: fleetQuery.data.total,
            totalPages: fleetQuery.data.totalPages,
            onPageChange: setPage,
          }}
          searchValue={search}
          onSearchChange={(value) => {
            setSearch(value)
            setPage(1)
          }}
          searchPlaceholder={t('fleet.pages.list.searchPlaceholder', 'Search by plate, brand, or model')}
          filters={filters}
          filterValues={filterValues}
          onFiltersApply={(next) => {
            setFilterValues(next)
            setPage(1)
          }}
          onFiltersClear={() => {
            setFilterValues({})
            setPage(1)
          }}
          rowActions={(row, helpers) => (
            <RowActions
              items={helpers.isEditing
                ? [
                    {
                      id: 'save',
                      label: helpers.isSaving
                        ? t('fleet.actions.saving', 'Saving…')
                        : t('fleet.actions.save', 'Save'),
                      onSelect: () => {
                        void helpers.saveEdit()
                      },
                    },
                    {
                      id: 'cancel',
                      label: t('fleet.actions.cancelEdit', 'Cancel'),
                      onSelect: helpers.cancelEdit,
                    },
                  ]
                : [
                    {
                      id: 'open',
                      label: t('fleet.actions.viewDetail', 'View detail'),
                      href: `/backend/fleet/${row.id}`,
                    },
                    {
                      id: 'edit',
                      label: t('fleet.actions.editInline', 'Edit inline'),
                      onSelect: helpers.startEdit,
                    },
                    {
                      id: 'delete',
                      label: t('fleet.actions.delete', 'Delete'),
                      onSelect: () => {
                        void handleDelete(row)
                      },
                      destructive: true,
                    },
                  ]}
            />
          )}
        />
      </PageBody>

      <FleetVehicleDialog
        open={createOpen}
        onOpenChange={(open) => {
          if (!createPending) setCreateOpen(open)
        }}
        mode="create"
        initialValues={toFleetVehicleFormValues(null)}
        loading={createPending}
        onSubmit={async (payload) => {
          setCreatePending(true)
          try {
            await createCrud('road/vehicles', payload, {
              errorMessage: t('fleet.messages.createError', 'Failed to create vehicle.'),
            })
            flash(t('fleet.messages.createSuccess', 'Vehicle created.'), 'success')
            setCreateOpen(false)
            await refreshFleet()
          } finally {
            setCreatePending(false)
          }
        }}
      />
      {ConfirmDialogElement}
    </Page>
  )
}
